/*
	Roles "de base" (Esteban, 2026-10-09, el día antes de apagar Zoho): asesoras, profesores, agentes de caja y de kits.
	La misma lista vive en el back (app/Common/RolesDeBase.php): cambiar las dos juntas.

	Quien SOLO tiene roles de esta lista:
	  - ve "Próximamente" en el Panel en vez del tablero del plantel;
	  - al ENTRAR (después del login, o al abrir WAPP en "/") cae directo en su herramienta:
	    Profesor → Asistencias, asesoras de ventas → CRM;
	  - en WappCloud solo ve lo suyo y lo compartido (eso lo decide el back).
	Si además tiene cualquier otro rol (supervisión, dirección…), gana el de más alcance: no cambia nada.
	Un enlace directo (sessionStorage "wapp.volverA", guardiaDeSesion.ts) gana sobre el aterrizaje por rol.
*/

export const ROL_PROFESOR = 3

/** Asesoras de ventas (entran al CRM): 8 Asesora Admisiones, 32 Agente de Ventas, 35 Asesora Reingresos, 38 Asesora de Ventas. */
export const ROLES_ASESORA_VENTAS = [8, 32, 35, 38]

/** Ids verificados contra ct_roles de producción el 2026-10-09. */
export const ROLES_SOLO_LO_SUYO = [
	3,    // Profesor
	8,    // Asesora Admisiones
	9,    // Agente Caja (Honduras)
	13,   // Agente Servicios Academicos
	15,   // Asesora Cobros
	18,   // Asesora Kits
	19,   // Agente Caja y Kits (México)
	30,   // Agente de Caja (El Salvador)
	31,   // Agente de Kits (México)
	32,   // Agente de Ventas
	35,   // Asesora Reingresos
	38,   // Asesora de Ventas
]

const NAVEGAR = 1

const idsDeRoles = (user: any): number[] =>
	Object.values(user?.roleMap || {}).map((r: any) => Number(r?.id)).filter((id: number) => !isNaN(id))

/** Sin roles (alumno) o con puros roles de base: el tablero del plantel no es para esta persona. */
export const panelProximamente = (user: any): boolean => {
	if (!user) return false
	return idsDeRoles(user).every((id) => ROLES_SOLO_LO_SUYO.includes(id))
}

/** ¿El rol con el que está navegando puede entrar a esa sección? (el menú se arma con ese rol). */
const puedeEntrar = (user: any, seccion: string): boolean => {
	const permiso = user?.roleMap?.[user?.currentRole]?.permits?.[seccion]
	return !!permiso && (Number(permiso.binary_quintet_RCUDN_permissions) & NAVEGAR) > 0
}

export interface Herramienta { ruta: string; nombre: string }

/** Su herramienta principal según el rol con el que entra (null = ninguna; se queda en el Panel). */
export const herramientaPrincipal = (user: any): Herramienta | null => {
	if (!user || !user.currentRole || user.currentRole === "student") return null
	const actual = Number(user.roleMap?.[user.currentRole]?.id)
	if (ROLES_ASESORA_VENTAS.includes(actual) && puedeEntrar(user, "prospects")) return { ruta: "/prospects", nombre: "CRM" }
	if (actual === ROL_PROFESOR && puedeEntrar(user, "attendances")) return { ruta: "/attendances", nombre: "Asistencias" }
	return null
}

/** A dónde mandarla al entrar: solo si TODOS sus roles son de base. */
export const aterrizajeDe = (user: any): string | null => {
	if (!user || idsDeRoles(user).length === 0 || !panelProximamente(user)) return null
	return herramientaPrincipal(user)?.ruta ?? null
}

/*
	"Al entrar" = la primera vez que se pinta el Panel desde que se cargó la página, si la página se cargó en "/" o en el
	login. Así, abrir app.washingtonacademy.mx o iniciar sesión la lleva a su herramienta, pero si después da clic en
	"Panel" (o en el logo) ve el Panel con "Próximamente". Se guarda en el módulo (no en sessionStorage) para que cada
	carga de página cuente como una entrada nueva.
*/
let pendiente: boolean = (() => {
	try {
		const p = window.location.pathname
		return p === "/" || p === "" || p.indexOf("/access") === 0
	} catch (e) {
		return false
	}
})()

export const aterrizajePendiente = (): boolean => pendiente

/** Ya entró a algún lado (su herramienta, o el enlace directo que traía): el Panel ya no redirige. */
export const aterrizajeHecho = (): void => { pendiente = false }
